import { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useConfirm } from '../../hooks/useConfirm';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { useI18n } from '../../i18n';
import { promoteTeamMember, demoteTeamMember, removeTeamMember } from '../../api/client';
import { apiError } from '../../hooks/useApiCall';
import ModalBackdrop from './ModalBackdrop';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import RoleBadge from './RoleBadge';
import CoachRoleSelect from './CoachRoleSelect';
import Field from './Field';
import type { TeamMemberRole } from '../../types';
import { personName } from '../../utils/personName';

export interface ManageableCoach {
  id: string;
  first_name: string;
  last_name: string;
  role: TeamMemberRole;
}

interface ManageCoachModalProps {
  teamId: string;
  teamName: string;
  coach: ManageableCoach;
  /** The reload of the parent. The modal waits for it (#605). */
  onUpdated: () => void | Promise<void>;
  onClose: () => void;
}

export default function ManageCoachModal({ teamId, teamName, coach, onUpdated, onClose }: ManageCoachModalProps) {
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const { t } = useI18n();
  const [role, setRole] = useState<TeamMemberRole>(coach.role);
  // #605 — Save and the trash share one lock. The lock holds until the reload
  // of the parent ends, because the modal shows until then.
  const write = useBusy();
  const fullName = personName(coach.first_name, coach.last_name);

  async function saveRole() {
    if (role === coach.role) { onClose(); return; }
    await write.run(async () => {
      let success: string;
      try {
        if (role === 'head_coach') {
          const res = await promoteTeamMember(teamId, coach.id);
          const handedOver = (res.data?.previous ?? []).length > 0;
          success = (handedOver ? t.teams.promotedHandover : t.teams.promoted).replace('{name}', fullName);
        } else {
          await demoteTeamMember(teamId, coach.id);
          success = t.teams.demoted.replace('{name}', fullName);
        }
      } catch (err) { showError(err); return; }
      // #419 — the change committed. A failed reload is a warning, and the modal closes.
      if (!await afterWrite({ source: 'ManageCoachModal:save', success, refresh: async () => onUpdated() })) onClose();
    });
  }

  async function remove() {
    // #605 — ask BEFORE the lock. The button is disabled while the lock is on, and the provider
    // gives no to a copy of an open question (a double tap).
    if (write.busy) return;
    if (!(await confirm({ message: t.teams.removeCoachConfirm.replace('{name}', fullName).replace('{team}', teamName), confirmLabel: t.teams.removeFromTeam, destructive: true }))) return;
    await write.run(async () => {
      try {
        await removeTeamMember(teamId, coach.id);
      } catch (err) { showError(err); return; }
      // #419 — the coach is out of the team. A failed reload is a warning, and the modal closes.
      const success = t.teams.removedFromTeam.replace('{name}', fullName);
      if (!await afterWrite({ source: 'ManageCoachModal:remove', success, refresh: async () => onUpdated() })) onClose();
    });
  }

  return (
    <ModalBackdrop onClose={onClose} maxWidth="sm" ariaLabel={t.modalLabels.manageCoach}>
      <ModalHeader
        title={t.modalLabels.manageCoach}
        subtitle={<span className="flex flex-wrap items-center gap-1.5">{fullName} <RoleBadge role={coach.role} size="xs" /></span>}
      />
      <div className="space-y-3">
        <div>
          <Field label={t.teams.assignRole}>
            <CoachRoleSelect value={role} onChange={setRole} disabled={write.busy} />
          </Field>
          {role === 'head_coach' && coach.role === 'coach' && (
            <p className="text-xs text-gray-500 mt-1" data-testid="promote-hands-over">{t.teams.promoteHandsOver}</p>
          )}
        </div>
      </div>
      <ModalFooter
        actions={[{ label: t.teams.removeFromTeam, onClick: remove, destructive: true, disabled: write.busy, testId: 'manage-coach-remove' }]}
        onSave={saveRole}
        busy={write.busy}
        saveDisabled={role === coach.role}
        onCancel={onClose}
      />
    </ModalBackdrop>
  );
}
